import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppContext, useAsyncData } from '@/hooks';
import { core } from '@/api/client';
import { formatRelative } from '@/utils/format';
import type { AdminDashboardData } from '@/types';
import { Icon, type IconName } from '@/components/icons';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import styles from './AdminDashboardPage.module.css';

const ACADEMIC_YEAR_STATUS_MAP: Record<string, string> = {
  SETUP_REQUIRED: 'setupRequired',
  DRAFT: 'draft',
  PENDING_APPROVAL: 'pendingApproval',
  ACTIVE: 'active',
  CLOSED: 'closed',
};

const INSTITUTION_STATUS_MAP: Record<string, string> = {
  ACTIVE: 'active',
  DRAFT: 'draft',
  SUSPENDED: 'suspended',
  CLOSED: 'closed',
};

const INTEGRATION_HEALTH_LABELS: Record<string, string> = {
  HEALTHY: 'Healthy',
  DEGRADED: 'Degraded',
  DOWN: 'Down',
  NOT_CONFIGURED: 'Not configured',
};

function integrationDotClass(status: string): string {
  switch (status) {
    case 'HEALTHY': return styles.dotHealthy;
    case 'DEGRADED': return styles.dotDegraded;
    case 'DOWN': return styles.dotDown;
    default: return styles.dotNotConfigured;
  }
}

function priorityClass(priority: string): string {
  switch (priority) {
    case 'HIGH': return styles.priorityHigh;
    case 'MEDIUM': return styles.priorityMedium;
    default: return styles.priorityLow;
  }
}

function statusBadgeClass(status: string): string {
  const key = status.toLowerCase();
  if (key === 'active') return styles.statusActive;
  if (key === 'draft') return styles.statusDraft;
  if (key === 'suspended') return styles.statusSuspended;
  if (key === 'closed') return styles.statusClosed;
  return styles.statusDraft;
}

function formatTimestamp(ts: string): string {
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return ts;
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return ts;
  }
}

export function AdminDashboardPage() {
  const { t, i18n } = useTranslation();
  const { activeContext } = useAppContext();
  const institution = activeContext?.institution;

  const { state, reload } = useAsyncData<AdminDashboardData>(
    `admin-dashboard-${institution?.id ?? 'none'}`,
    () => core<AdminDashboardData>(`/admin/institution/${institution?.id}/dashboard`),
    !!institution,
  );

  if (state.status === 'loading') {
    return (
      <div className={styles.page} aria-busy="true">
        <header className={styles.header}>
          <div className={styles.headerInfo}>
            <h1 className={styles.headerName}>{institution?.name ?? t('admin.dashboard.title')}</h1>
          </div>
        </header>
        <div className={styles.metricsGrid}>
          {[0, 1, 2, 3].map((i) => (
            <Card key={i} className={styles.metricCard}>
              <div className={styles.metricCardInner}>
                <Skeleton width="3rem" height="1.75rem" />
                <Skeleton width="70%" height="0.8rem" />
                <Skeleton width="50%" height="0.8rem" />
              </div>
            </Card>
          ))}
        </div>
        <div className={styles.contentGrid}>
          {[0, 1].map((i) => (
            <Card key={i} className={styles.sectionCard}>
              <Skeleton width="40%" height="1rem" />
              <div className={styles.skeletonLines}>
                <Skeleton height="0.85rem" />
                <Skeleton height="0.85rem" width="85%" />
                <Skeleton height="0.85rem" width="70%" />
              </div>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (state.status === 'error') {
    const error = state.error;

    if (error.isMissingEndpoint) {
      return (
        <div className={styles.page}>
          <Card className={styles.emptyState}>
            <Icon name="noProducts" size={48} />
            <h2 className={styles.emptyStateTitle}>{t('admin.dashboard.title')}</h2>
            <p className={styles.emptyStateBody}>
              {t('platform.failure.missing.body')}
            </p>
            <button className={styles.retryButton} onClick={reload}>
              {t('common.retry')}
            </button>
          </Card>
        </div>
      );
    }

    if (error.isForbidden) {
      return (
        <div className={styles.page}>
          <Card className={styles.emptyState}>
            <Icon name="noAccess" size={48} />
            <h2 className={styles.emptyStateTitle}>{t('admin.dashboard.title')}</h2>
            <p className={styles.emptyStateBody}>
              {t('platform.failure.forbidden.body')}
            </p>
            <button className={styles.retryButton} onClick={reload}>
              {t('common.retry')}
            </button>
          </Card>
        </div>
      );
    }

    if (error.isUnreachable) {
      return (
        <div className={styles.page}>
          <Card className={styles.emptyState}>
            <Icon name="error" size={48} />
            <h2 className={styles.emptyStateTitle}>{t('admin.dashboard.title')}</h2>
            <p className={styles.emptyStateBody}>
              {t('platform.failure.unreachable.body')}
            </p>
            <button className={styles.retryButton} onClick={reload}>
              {t('common.retry')}
            </button>
          </Card>
        </div>
      );
    }

    return (
      <div className={styles.page}>
        <Card className={styles.emptyState}>
          <Icon name="error" size={48} />
          <h2 className={styles.emptyStateTitle}>{t('admin.dashboard.title')}</h2>
          <p className={styles.emptyStateBody}>
            {t('platform.failure.generic.body')}
          </p>
          <button className={styles.retryButton} onClick={reload}>
            {t('common.retry')}
          </button>
        </Card>
      </div>
    );
  }

  const data = state.data;

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInfo}>
          <h1 className={styles.headerName}>
            {institution?.name ?? data.institution.name}
          </h1>
          <div className={styles.headerMeta}>
            <span
              className={`${styles.statusBadge} ${statusBadgeClass(data.institution.status)}`}
            >
              {t(
                `admin.status.${INSTITUTION_STATUS_MAP[data.institution.status] ?? 'active'}`,
                data.institution.status.toLowerCase(),
              )}
            </span>
            <span className={styles.headerCode}>
              {data.institution.code}
            </span>
          </div>
        </div>
      </header>

      <div className={styles.metricsGrid}>
        <MetricCard
          value={data.campuses.active}
          sub={`/ ${data.campuses.total} ${t('admin.dashboard.campuses').toLowerCase()}`}
          label={t('admin.dashboard.campuses')}
          to="/admin/institution/campuses"
        />
        <MetricCard
          value={data.academicYear?.label ?? '—'}
          sub={data.academicYear
            ? t(
                `admin.academicYear.${ACADEMIC_YEAR_STATUS_MAP[data.academicYear.status] ?? 'setupRequired'}`,
              )
            : t('admin.academicYear.setupRequired')}
          label={t('admin.dashboard.academicYear')}
          to="/admin/institution/academic-years"
        />
        <MetricCard
          value={
            data.products.length > 0
              ? data.products.filter((p) => p.status === 'ENABLED').length
              : 0
          }
          sub={data.products.length > 0
            ? `/ ${data.products.length} ${t('admin.dashboard.products').toLowerCase()}`
            : undefined}
          label={t('admin.dashboard.products')}
          to="/admin/institution/products"
        />
        <MetricCard
          value={data.users.active}
          sub={`/ ${data.users.total} ${t('admin.dashboard.users').toLowerCase()} · ${data.users.pendingInvitations} ${t('admin.dashboard.pendingInvitations')}`}
          label={t('admin.dashboard.users')}
          to="/admin/institution/users"
        />
      </div>

      <div className={styles.contentGrid}>
        <Card className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t('admin.dashboard.needsAttention')}</h2>
          {data.pendingTasks.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.dashboard.noData')}</p>
          ) : (
            <ul className={styles.taskList}>
              {data.pendingTasks.map((task) => (
                <li key={task.id} className={styles.taskItem}>
                  <span className={`${styles.taskPriority} ${priorityClass(task.priority)}`} />
                  <div className={styles.taskContent}>
                    <p className={styles.taskTitle}>
                      {task.targetUrl ? (
                        <a href={task.targetUrl} className={styles.taskLink}>
                          {task.title}
                        </a>
                      ) : (
                        task.title
                      )}
                    </p>
                    <span className={styles.taskCategory}>{task.category}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className={styles.sectionCard}>
          <h2 className={styles.sectionTitle}>{t('admin.dashboard.integrationHealth')}</h2>
          {data.integrationHealth.length === 0 ? (
            <p className={styles.emptyStateBody}>{t('admin.dashboard.noData')}</p>
          ) : (
            <ul className={styles.integrationList}>
              {data.integrationHealth.map((svc, idx) => (
                <li key={`${svc.service}-${idx}`} className={styles.integrationItem}>
                  <span className={styles.integrationName}>{svc.service}</span>
                  <span className={styles.integrationStatus}>
                    <span className={`${styles.statusDot} ${integrationDotClass(svc.status)}`} />
                    {INTEGRATION_HEALTH_LABELS[svc.status] ?? svc.status}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className={styles.sectionCard}>
        <h2 className={styles.sectionTitle}>{t('admin.dashboard.recentChanges')}</h2>
        {data.recentChanges.length === 0 ? (
          <p className={styles.emptyStateBody}>{t('admin.dashboard.noData')}</p>
        ) : (
          <ul className={styles.changeList}>
            {data.recentChanges.map((chg, idx) => (
              <li key={`${chg.timestamp}-${idx}`} className={styles.changeItem}>
                <div className={styles.changeInfo}>
                  <p className={styles.changeAction}>{chg.action}</p>
                  <span className={styles.changeActor}>{chg.actor}</span>
                </div>
                <time className={styles.changeTime} title={formatTimestamp(chg.timestamp)}>
                  {formatRelative(chg.timestamp, i18n.language)}
                </time>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <section>
        <h2 className={styles.sectionTitle}>{t('admin.dashboard.quickLinks')}</h2>
        <div className={styles.quickLinksGrid}>
          <QuickLinkCard icon="profile" label={t('admin.nav.profile')} to="/admin/institution/profile" />
          <QuickLinkCard icon="users" label={t('admin.nav.users')} to="/admin/institution/users" />
          <QuickLinkCard icon="campus" label={t('admin.nav.campuses')} to="/admin/institution/campuses" />
          <QuickLinkCard icon="branding" label={t('admin.nav.branding')} to="/admin/institution/branding" />
        </div>
      </section>
    </div>
  );
}

function MetricCard({
  value,
  sub,
  label,
  to,
}: {
  value: string | number;
  sub?: string;
  label: string;
  to?: string;
}) {
  const navigate = useNavigate();
  const body = (
    <div className={styles.metricCardInner}>
      <div className={styles.metricValue}>{value}</div>
      {sub && <div className={styles.metricSub}>{sub}</div>}
      <div className={styles.metricLabel}>{label}</div>
    </div>
  );
  if (!to) return <Card className={styles.metricCard}>{body}</Card>;
  return (
    <Card className={`${styles.metricCard} ${styles.metricCardLink}`}>
      <button
        type="button"
        className={styles.metricCardButton}
        aria-label={label}
        onClick={() => navigate(to)}
      >
        {body}
      </button>
    </Card>
  );
}

function QuickLinkCard({ icon, label, to }: { icon: IconName; label: string; to: string }) {
  const navigate = useNavigate();
  return (
    <button
      className={styles.quickLinkCard}
      type="button"
      aria-label={label}
      onClick={() => navigate(to)}
    >
      <span className={styles.quickLinkIcon}>
        <Icon name={icon} size={28} />
      </span>
      <span className={styles.quickLinkLabel}>{label}</span>
    </button>
  );
}
